import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import Slider from "rc-slider"
import "rc-slider/assets/index.css"
import { FFmpeg } from "@ffmpeg/ffmpeg"
import { fetchFile } from "@ffmpeg/util"
import styles from "./index.module.css"
import { PlayIcon } from "ui/MediaPreview/PlayIcon"
import { useTranslation } from "react-i18next"

// Simple time formatter mm:ss or hh:mm:ss
const fmt = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const ss = s % 60
  const two = (n) => String(n).padStart(2, "0")
  return h > 0 ? `${two(h)}:${two(m)}:${two(ss)}` : `${two(m)}:${two(ss)}`
}

export default function VideoTrim({ src, maxTrim = 300, onChange }) {
  const { t } = useTranslation()
  const playerRef = useRef(null) // HTMLVideoElement
  const [playerState, setPlayerState] = useState({})
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [range, setRange] = useState([0, 0])
  const [thumbs, setThumbs] = useState([])
  const ffmpegRef = useRef(null)
  const prevThumbsRef = useRef([])
  const lastRangeRef = useRef([0, 0])
  const [validationMsg, setValidationMsg] = useState("")
  const pendingSeekRef = useRef(null) // { index: 0|1 } indicates which handle we last programmatically sought

  // Initialize range once duration is known
  useEffect(() => {
    if (duration > 0 && range[1] === 0) {
      setRange([0, Math.min(maxTrim, duration)])
    }
  }, [duration, maxTrim, range])

  // Emit changes
  useEffect(() => {
    onChange?.({ start: range[0], end: range[1], duration })
  }, [range, duration, onChange])

  // Setup HTMLVideoElement listeners to mirror player state
  useEffect(() => {
    const video = playerRef.current
    if (!video) return undefined

    const onLoadedMetadata = () => {
      const d = Number.isFinite(video.duration) ? video.duration : 0
      if (d && Math.abs(d - duration) > 0.005) {
        setDuration(d)
        setRange((r) => [Math.max(0, Math.min(r[0], d)), Math.max(0, Math.min(r[1], d))])
      }
    }
    const onTimeUpdate = () => {
      const cur = Number.isFinite(video.currentTime) ? video.currentTime : 0
      setPlayerState((s) => ({ ...s, currentTime: cur, seeking: video.seeking, paused: video.paused }))
      // Loop within [start, end)
      if (duration > 0 && cur > range[1] - 0.005) {
        try { video.currentTime = Math.max(0, range[0]) } catch {}
      }
    }
    const onPlay = () => {
      setIsPlaying(true)
    }
    const onPause = () => {
      setIsPlaying(false)
    }
    const onSeeking = () => {
      setPlayerState((s) => ({ ...s, seeking: true }))
    }
    const onSeeked = () => {
      const ct = Number.isFinite(video.currentTime) ? video.currentTime : 0
      if (pendingSeekRef.current) {
        const idx = pendingSeekRef.current.index
        if (idx === 0 || idx === 1) {
          const d = duration || video.duration || 0
          const clamped = Math.max(0, Math.min(ct, d))
          setRange((r) => {
            const next = [...r]
            next[idx] = Math.round(clamped * 100) / 100
            const dd = d || next[1]
            next[0] = Math.max(0, Math.min(next[0], dd))
            next[1] = Math.max(next[0], Math.min(next[1], dd))
            lastRangeRef.current = next
            return next
          })
        }
        pendingSeekRef.current = null
      }
    }

    video.addEventListener("loadedmetadata", onLoadedMetadata)
    video.addEventListener("timeupdate", onTimeUpdate)
    video.addEventListener("seeking", onSeeking)
    video.addEventListener("seeked", onSeeked)
    video.addEventListener("play", onPlay)
    video.addEventListener("pause", onPause)

    return () => {
      video.removeEventListener("loadedmetadata", onLoadedMetadata)
      video.removeEventListener("timeupdate", onTimeUpdate)
      video.removeEventListener("seeking", onSeeking)
      video.removeEventListener("seeked", onSeeked)
      video.removeEventListener("play", onPlay)
      video.removeEventListener("pause", onPause)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration, range])

  // Initialize and generate thumbnails with ffmpeg.wasm
  useEffect(() => {
    let cancelled = false
    if (!src) return

    const ensureFfmpeg = async () => {
      if (!ffmpegRef.current) {
        const ffmpeg = new FFmpeg()
        await ffmpeg.load()
        ffmpegRef.current = ffmpeg
      }
      return ffmpegRef.current
    }

    const generate = async () => {
      try {
        const ffmpeg = await ensureFfmpeg()
        if (cancelled) return
        // Write input file
        const data = await fetchFile(src)
        const inputName = "input"
        const ext = typeof src === "string" && src.includes(".") ? src.split(".").pop().split("?")[0] : "mp4"
        const inputFile = `${inputName}.${ext}`
        await ffmpeg.writeFile(inputFile, data)

        // Decide frame count
        const frames = 12
        const dur = Math.max(1, duration || 1)
        const step = dur / frames

        const framePromises = Array.from({ length: frames }, async (_, i) => {
          if (cancelled) return null;
          
          const t = Math.max(0, i * step);
          const out = `thumb_${i}.jpg`;
          
          try {
            await ffmpeg.exec([
              "-ss", `${t}`,
              "-i", inputFile,
              "-frames:v", "1",
              "-vf", "scale=160:-1",
              "-q:v", "4",
              out
            ]);
            
            const bytes = await ffmpeg.readFile(out);
            return new Blob([bytes.buffer ?? bytes], { type: "image/jpeg" });
          } catch (error) {
            console.error(`Error processing frame ${i}:`, error);
            return null;
          }
        });
        
        const blobs = (await Promise.all(framePromises)).filter(Boolean);
        const urls = blobs.map(blob => URL.createObjectURL(blob));
        if (!cancelled) {
          // Revoke any previous URLs to avoid memory leaks
          try { for (const u of prevThumbsRef.current) { URL.revokeObjectURL(u) } } catch {}
          setThumbs(urls)
          prevThumbsRef.current = urls
        }
      } catch (err) {
        // Handle exception: log for diagnostics and reset thumbnails safely
        console.error("VideoTrim: thumbnail generation failed", err)
        if (!cancelled) setThumbs([])
      }
    }

    generate()
    return () => {
      cancelled = true
  try { for (const u of prevThumbsRef.current) { URL.revokeObjectURL(u) } } catch {}
      prevThumbsRef.current = []
    }
    // We regenerate if src changes or duration becomes available
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, duration])

  // No overlay controls; ControlBar handles play/pause

  const onSliderChange = (vals) => {
    const [rawS, rawE] = vals
    const [ps, pe] = lastRangeRef.current

    const clamp = (v) => {
      const d = Math.max(0, duration || 0)
      const vv = Math.min(Math.max(0, v), d)
      return Math.round(vv * 100) / 100
    }

    const detectMoved = (rs, re, prevS, prevE) => {
      const EPS = 0.0005
      const dS = Math.abs((rs ?? prevS) - prevS)
      const dE = Math.abs((re ?? prevE) - prevE)
      if (dS > EPS && dE <= EPS) return 0
      if (dE > EPS && dS <= EPS) return 1
      return dS > dE ? 0 : 1
    }

    const applyMaxTrim = (s, e, movedIdx) => {
      if (!maxTrim || e - s <= maxTrim) return [s, e]
      return movedIdx === 0 ? [s, s + maxTrim] : [e - maxTrim, e]
    }

    const updateValidation = (exceeded) => {
      if (exceeded) setValidationMsg(t("listings.tabs.media.max_trim_error"))
      else if (validationMsg) setValidationMsg("")
    }

    // 1) Decide which handle moved
    const moved = detectMoved(rawS, rawE, ps, pe)

    // 2) Enforce maxTrim based on raw values (for UX feedback) and adjust s/e accordingly
    const exceededRaw = Boolean(maxTrim && rawE - rawS > maxTrim)
    updateValidation(exceededRaw)
    let [s, e] = applyMaxTrim(rawS, rawE, moved)

    // 3) Clamp to video duration bounds
    s = clamp(s)
    e = clamp(e)

    // 4) Persist range and seek for exact sync
    setRange([s, e])
    lastRangeRef.current = [s, e]

    const player = playerRef.current
    const seekTarget = moved === 0 ? s : e
    if (player && Number.isFinite(seekTarget)) {
      try { player.pause() } catch {}
      try { player.currentTime = seekTarget } catch {}
    }
  }

  const onSliderAfterChange = (vals) => {
    // final reconciliation: seek again to final clamped value
    const [fs, fe] = lastRangeRef.current
    const [rs, re] = vals
    const moved = Math.abs(rs - fs) > Math.abs(re - fe) ? 0 : 1
    const target = moved === 0 ? fs : fe
    const player = playerRef.current
    if (player && Number.isFinite(target)) {
      try { player.pause() } catch {}
      try { player.currentTime = target } catch {}
    }
  }
  // rc-slider: use new `styles` prop instead of deprecated railStyle/trackStyle/handleStyle

  const handleVideoToggle = (e) => {
    e?.preventDefault?.()
    const v = playerRef.current
    if (!v) return
    if (v.paused) {
      v.play().catch(() => {})
    } else {
      try { v.pause() } catch {}
    }
  }

  return (
    <div className={styles.container}>
      {/* Visible player using native video */}
      <div className={styles.playerWrap}>
        <video
          ref={playerRef}
          src={src}
          controls
          playsInline
          onClick={handleVideoToggle}
          className={styles.playerVideo}
        >
          <track
            kind="captions"
            src="/blank.vtt"
            srcLang="en"
            label="English"
            default
          />
        </video>
        {!isPlaying && (
          <PlayIcon onClick={handleVideoToggle} />
        )}
      </div>

      {/* Thumbnail rail + range slider */}
      <div className={styles.trimWrap}>
        <div className={styles.filmstrip}>
          {/* Thumbnails strip */}
          {thumbs.length > 0 && (
            <div className={styles.thumbs}>
              {thumbs.map((src) => (
                <img key={src} src={src} alt="thumb" style={{ width: `${100 / thumbs.length}%` }} />
              ))}
            </div>
          )}
          {/* Playhead line */}
          {duration > 0 && (
            <div
              className={styles.playhead}
              style={{ left: `${((playerState?.currentTime ?? 0) / Math.max(0.1, duration)) * 100}%` }}
            />
          )}
          {/* Blue rounded selection outline (visual only) */}
          {duration > 0 && (
            <div
              className={styles.selection}
              style={{
                left: `${(range[0] / Math.max(0.1, duration)) * 100}%`,
                width: `${((range[1] - range[0]) / Math.max(0.1, duration)) * 100}%`
              }}
            />
          )}
          {/* End-cap white handles (decorative) */}
          {duration > 0 && (
            <>
              <div aria-hidden className={styles.cap}
                   style={{ left: `calc(${(range[0] / Math.max(0.1, duration)) * 100}% - 5px)` }} />
              <div aria-hidden className={styles.cap}
                   style={{ left: `calc(${(range[1] / Math.max(0.1, duration)) * 100}% - 5px)` }} />
            </>
          )}
          {/* Slider overlay */}
          <div className={styles.sliderOverlay}>
            <Slider
              range={{ allowCross: false }}
              min={0}
              max={Math.max(0.1, duration)}
              step={0.01}
              value={range}
              onChange={onSliderChange}
              onChangeComplete={onSliderAfterChange}
              styles={{
                rail: {
                  height: 48,
                  borderRadius: 10,
                  background: "transparent"
                },
                // Make the internal track transparent; we draw our own blue outline above
                track: { height: 28, background: "transparent" },
                handle: {
                  backgroundColor: "transparent",
                  borderColor: "transparent",
                  borderWidth: 0,
                  height: 36,
                  width: 14,
                  borderRadius: 6,
                  marginTop: -4,
                  boxShadow: "none",
                  opacity: 0
                }
              }}
            />
          </div>
        </div>
        {/* Time labels */}
        <div className={styles.timeRow}>
          <span className={styles.timeMuted}>{fmt(range[0])}</span>
          <strong className={styles.timeStrong}>{fmt(Math.max(0, range[1] - range[0]))}</strong>
          <span className={styles.timeMuted}>{fmt(range[1])}</span>
        </div>
        {validationMsg && (
          <div role="alert" className={styles.alertMsg}>
            {validationMsg}
          </div>
        )}
      </div>
    </div>
  )
}

VideoTrim.propTypes = {
  src: PropTypes.string.isRequired,
  maxTrim: PropTypes.number,
  onChange: PropTypes.func
}
